import {
  Component,
  Suspense,
  type ErrorInfo,
  type ReactElement,
  type ReactNode,
} from "react";
import { RefreshCw } from "lucide-react";
import { useLocation } from "react-router-dom";
import { AsyncSection, Button, EmptyState, Skeleton } from "@playbookai/ui";

interface ModuleRouteBoundaryProps {
  readonly children: ReactNode;
  readonly label: string;
}

interface ModuleErrorBoundaryState {
  readonly hasError: boolean;
}

/**
 * Owns the asynchronous boundary for every lazy product module. Keeping this
 * in the shell prevents a failed module chunk or render from taking down the
 * surrounding navigation and gives every module the same recovery contract.
 */
export function ModuleRouteBoundary({
  children,
  label,
}: ModuleRouteBoundaryProps): ReactElement {
  const { pathname } = useLocation();

  return (
    <ModuleErrorBoundary key={pathname} label={label}>
      <Suspense fallback={<ModuleLoading label={label} />}>{children}</Suspense>
    </ModuleErrorBoundary>
  );
}

export function ModuleLoading({ label }: { label: string }): ReactElement {
  /* The loading branch of AsyncSection: its `role="status"` region is named
     "Loading {label}" and holds the content-shaped skeleton. */
  return (
    <div className="bg-pb-page px-5 pb-24 pt-5 text-pb-text sm:px-8 sm:py-6 lg:px-10">
      <AsyncSection
        status="loading"
        loadingLabel={label}
        errorTitle={`${label} couldn’t be opened`}
        skeleton={<ModuleLoadingSkeleton label={label} />}
      >
        {null}
      </AsyncSection>
    </div>
  );
}

function ModuleLoadingSkeleton({ label }: { label: string }): ReactElement {
  return (
    <>
      <span className="sr-only">Loading {label}…</span>
      <div aria-hidden="true" className="mx-auto w-full max-w-7xl">
        <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(20rem,0.68fr)] lg:gap-10">
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Skeleton className="h-9 w-52 sm:w-64" />
              <Skeleton className="hidden h-5 w-40 sm:block" />
            </div>
            <Skeleton className="h-5 w-full max-w-2xl" />
            <Skeleton className="h-5 w-3/5 max-w-lg" />
          </div>
          <Skeleton className="h-11 w-full lg:mt-1" />
        </div>

        {/* Kept hand-built: Card draws its border at 80% and adds the top
            hairline, where this placeholder has today's full pb-border. */}
        <div className="mt-6 overflow-hidden rounded-xl border border-pb-border bg-pb-card">
          <div className="flex items-center gap-3 border-b border-pb-border px-4 py-3">
            <Skeleton className="h-5 w-36" />
            <Skeleton className="h-4 w-52" />
          </div>
          <div className="grid md:grid-cols-3">
            {[0, 1, 2].map((item) => (
              <div
                key={item}
                className="flex items-center gap-3 border-b border-pb-border px-4 py-4 last:border-b-0 md:border-b-0 md:border-r md:last:border-r-0"
              >
                <Skeleton shape="panel" className="h-10 w-10 shrink-0" />
                <div className="min-w-0 flex-1 space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-full max-w-52" />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 grid gap-8 border-t border-pb-border pt-7 lg:grid-cols-[minmax(0,1.65fr)_minmax(18rem,0.75fr)] lg:gap-12">
          <div className="space-y-4">
            <Skeleton className="h-7 w-56" />
            <Skeleton shape="card" className="h-24 w-full" />
            <Skeleton shape="card" className="h-24 w-full" />
          </div>
          <div className="space-y-4">
            <Skeleton className="h-7 w-44" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        </div>
      </div>
    </>
  );
}

export function ModuleErrorState({
  label,
  onRetry,
}: {
  readonly label: string;
  readonly onRetry: () => void;
}): ReactElement {
  return (
    <div className="bg-pb-page px-5 py-6 text-pb-text sm:px-8 sm:py-8 lg:px-10">
      <div className="mx-auto flex min-h-[calc(100dvh-6rem)] w-full max-w-6xl items-center justify-center sm:min-h-[calc(100dvh-7rem)]">
        {/* The page-level error screen: EmptyState lg (OK-33) in today's
            frame. The h1 stays this page's own, inside the EmptyState title.
            The frame is kept hand-built: Card takes a 14px corner, an 80%
            border and its 1px inset top hairline, a look change no PR 3 row
            approves. */}
        <section
          role="alert"
          aria-labelledby="module-error-heading"
          className="w-full max-w-xl rounded-2xl border border-pb-border bg-pb-card"
        >
          <EmptyState
            variant="error"
            size="lg"
            title={
              <h1 id="module-error-heading">{label} couldn’t be opened</h1>
            }
            description={
              <>
                The module did not finish loading. Reload it to try again. Your
                workspace data has not been changed.
                <span className="mt-2 block text-xs text-pb-text-muted">
                  If this keeps happening, contact your admin.
                </span>
              </>
            }
            action={
              <Button variant="secondary" onClick={onRetry}>
                <RefreshCw aria-hidden="true" className="h-4 w-4" />
                Reload {label}
              </Button>
            }
          />
        </section>
      </div>
    </div>
  );
}

export class ModuleErrorBoundary extends Component<
  ModuleRouteBoundaryProps,
  ModuleErrorBoundaryState
> {
  public override state: ModuleErrorBoundaryState = { hasError: false };

  public static getDerivedStateFromError(): ModuleErrorBoundaryState {
    return { hasError: true };
  }

  public override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(`Failed to render ${this.props.label}`, error, info);
  }

  public override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <ModuleErrorState
          label={this.props.label}
          onRetry={() => window.location.reload()}
        />
      );
    }

    return this.props.children;
  }
}
